import { Controller } from '@hotwired/stimulus'

import * as analytics from '../utils/analytics'

// The naming convention is: provenance:objectName_actionName
// Example event name:       provenance:bundleTab_clicked
// This style is documented: https://davidwells.io/blog/clean-analytics
export default class extends Controller {
  // For improved clarity, we should prefer values over plain data attributes.
  // E.g. use data-analytics-label-value="Some Label" over data-label="Some Label"
  // See: https://stimulus.hotwired.dev/reference/values
  static values = {
    // The action name in past tense, e.g. clicked, or scrolled.
    // It is used to construct the event name.
    action: { type: String, default: 'clicked' },
    // The context, which follows a specific format.
    // E.g.: Retailer Name / Beauty brand / Sun Cream
    // TODO: Refactor this to be taken from the top level (more convenient than having to pass it through).
    label: String,
    // The name of the object being tracked.
    // It is used to construct the event name.
    name: String,
    // Subject can contain any useful info, e.g. the name of the PP being clicked.
    subject: String,
    // The version is currently only used to track which UI version of a claim
    // modal is being used, but it could be used for other purposes in the future.
    version: Number
  }

  // Use the click() method to track clicks.
  // Assumes that the event.currentTarget has type and label data attributes (for legacy reasons).
  //
  // Example usage:
  //
  //   <div
  //     class="Feedback"
  //     data-controller="analytics"
  //     data-analytics-name-value="feedback"
  //     data-analytics-label-value="Douglas / Dior / Lipgloss"
  //   >
  //     <button
  //       data-action="analytics#click"
  //       data-analytics-subject-value="positive"
  //     > 👍
  //     </button>
  //     <button
  //       data-action="analytics#click"
  //       data-analytics-subject-value="negative"
  //     > 👎
  //     </button>
  //   </div>
  //
  click (event) {
    const { context, label, type: eventType, brandName, subjectName } = event.currentTarget.dataset
    const { channel, type } = this.element.dataset
    let prodOrBrand
    let ppSubjectType
    let bundleType
    let dtc

    if (subjectName && brandName) {
      prodOrBrand = (subjectName === brandName) ? 'brand' : 'product'
    }
    if (channel && brandName) {
      dtc = channel === brandName
    }
    // if combined bundle, check whether product or brand tab selected
    if (type === 'combined_bundle') {
      bundleType = type
      const tabName = this.element.querySelector("[aria-selected='true']").dataset.name
      ppSubjectType = (tabName === 'product-pp-tab') ? 'product' : 'brand'
      // else take from subjectName equaling brandName
    } else {
      bundleType = prodOrBrand
      ppSubjectType = prodOrBrand
    }

    let eventName = `click_${eventType}`
    if (type) eventName += `_${type}`

    if (this.element.nodeName === 'DETAILS') {
      this.actionValue = this.element.open ? 'opened' : 'closed'
    }

    if (this.hasNameValue) {
      eventName = `${this.nameValue}_${this.actionValue}`
    }

    const eventLabel = channel ? `${channel} / ${context}` : context

    analytics.track(eventName, {
      eventAction: this.hasSubjectValue ? this.subjectValue : label,
      eventLabel: this.hasLabelValue ? this.labelValue : eventLabel
    }, { adapter: 'gtm' })

    eventName = eventType ? `${eventType}_${this.actionValue}` : eventName

    analytics.track(eventName, {
      object: eventType,
      action: this.actionValue,
      type,
      channel,
      brand_name: brandName,
      subject_name: subjectName,
      subject_value: this.hasSubjectValue ? this.subjectValue : label,
      subject_type: ppSubjectType,
      event_label: eventLabel,
      bundle_type: bundleType,
      dtc,
      ...(this.hasVersionValue && { ui_version: this.versionValue })
    }, { adapter: 'segment' })
  }
}
